import { Button, DropdownMenu } from 'components/atoms';
import { TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { PiCalendar, PiChartBar, PiCurrencyDollar, PiSquaresFour } from 'react-icons/pi';
import { useSelectedView, useSetSelectedView, useTokenData } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { CalendarTab, SpotifyStatisticsTab, FinanceStatisticsTab } from './tabs';

type ViewType = 'calendar' | 'spotify-statistics' | 'finance-statistics';

interface IViewOption {
  id: ViewType;
  label: string;
  icon: React.ReactNode;
}

const Home = () => {
  const { t } = useTranslation();
  const selectedView = useSelectedView();
  const setSelectedView = useSetSelectedView();
  const dropdownVisible = useComponentVisible<HTMLDivElement>(false);
  const tokenData = useTokenData();

  const allViewOptions: IViewOption[] = [
    {
      id: 'calendar',
      label: t('navbar.homeViews.calendar'),
      icon: <PiCalendar className="w-4 h-4" />
    },
    {
      id: 'spotify-statistics',
      label: t('navbar.homeViews.spotify-statistics'),
      icon: <PiChartBar className="w-4 h-4" />
    },
    {
      id: 'finance-statistics',
      label: t('navbar.homeViews.finance-statistics'),
      icon: <PiCurrencyDollar className="w-4 h-4" />
    }
  ];

  const viewOptions = allViewOptions.filter(option => {
    if (option.id === 'spotify-statistics') {
      return !!tokenData?.StatisticsPermissions;
    }
    if (option.id === 'finance-statistics') {
      return !!tokenData?.FinanceStatisticPermissions;
    }
    return true;
  });

  useEffect(() => {
    if (
      selectedView === 'spotify-statistics' &&
      !tokenData?.StatisticsPermissions
    ) {
      setSelectedView('calendar');
    }
    if (
      selectedView === 'finance-statistics' &&
      !tokenData?.FinanceStatisticPermissions
    ) {
      setSelectedView('calendar');
    }
  }, [
    selectedView,
    tokenData?.StatisticsPermissions,
    tokenData?.FinanceStatisticPermissions,
    setSelectedView,
  ]);

  const currentView = viewOptions.find(option => option.id === selectedView);

  const renderSelectedView = () => {
    switch (selectedView) {
      case 'calendar':
        return <CalendarTab />;
      case 'spotify-statistics':
        if (tokenData?.StatisticsPermissions) {
          return <SpotifyStatisticsTab />;
        }
        return <CalendarTab />;
      case 'finance-statistics':
        if (tokenData?.FinanceStatisticPermissions) {
          return <FinanceStatisticsTab />;
        }
        return <CalendarTab />;
      default:
        return <CalendarTab />;
    }
  };

  return (
    <TopbarPage 
      topbar={
        <TopBar 
          title={`${t('navbar.home')} - ${currentView?.label}`} 
          hideBackButton 
          topbarContent={
            <div className="flex items-center gap-4">
              <DropdownMenu
                ref={dropdownVisible.ref}
                visible={dropdownVisible.isComponentVisible}
                style={{ padding: 5, zIndex: 9999 }}
                justifyPosition={'end'}>
                <div className="flex flex-col w-full">
                  {viewOptions.map((option) => (
                    <Button
                      key={option.id}
                      isClear
                      fullWidth
                      onClick={() => {
                        setSelectedView(option.id);
                        dropdownVisible.handleComponentVisible();
                      }}
                      className={`flex items-center gap-3 ${
                        selectedView === option.id ? 'bg-blue-50 text-blue-700' : ''
                      }`}>
                      {option.icon}
                      <Button.Text text={option.label} />
                    </Button>
                  ))}
                </div>
              </DropdownMenu>
              <Button
                variant={BUTTON_VARIANT.default}
                isClear
                onClick={dropdownVisible.handleComponentVisible}>
                <Button.Icon icon={<PiSquaresFour />} />
                <Button.Text text={t('navbar.changeView', 'Zmień widok')} />
              </Button>
            </div>
          }
        />
      }>
        {renderSelectedView()}
    </TopbarPage>
  );
};

export default Home;
